import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Terms & Conditions | Audio & Co®" description="Terms of service for Audio & Co® including SEO management, website development, free SEO audit tool, podcast production, and publishing services." />
      {/* Header */}
      <section className="bg-black text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mb-6 border-4 border-yellow-400">
              <FileText className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black mb-4">
              <span className="text-red-600">Terms &</span> <span className="text-yellow-400">Conditions</span>
            </h1>
            <p className="text-xl text-yellow-400 font-bold">
              The terms governing our professional media services and business relationship.
            </p>
            <p className="text-sm text-white mt-4">
              Last updated: April 2026 | Effective immediately
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            
            {/* Important Notice */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
              <div className="flex">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-black text-yellow-800 mb-2">Important Notice</h3>
                  <p className="text-yellow-700 text-sm font-bold">
                    Please read these terms carefully before using our services. By engaging Audio & Co.® for any services, 
                    you agree to be bound by these terms and conditions.
                  </p>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2 className="text-red-600 font-black">1. Agreement Overview</h2>
              
              <p className="text-gray-900">
                These Terms and Conditions ("Terms") govern your use of services provided by Audio & Co.® 
                ("Audio & Co.®," "we," "us," or "our"). 
                By engaging our services, you ("Client" or "you") agree to these Terms.
              </p>

              <h2 className="text-red-600 font-black">2. Services Provided</h2>
              
              <h3 className="text-yellow-600 font-bold">Audio Services</h3>
              <ul>
                <li>Podcast strategy development and positioning</li>
                <li>Podcast production, editing, and mastering</li>
                <li>Podcast launch and marketing campaigns</li>
                <li>Ongoing podcast management and optimisation</li>
                <li>Audio content creation and distribution</li>
              </ul>

              <h3 className="text-yellow-600 font-bold">Video Services</h3>
              <ul>
                <li>YouTube channel setup and optimisation</li>
                <li>Video content creation and editing</li>
                <li>YouTube marketing and growth strategies</li>
                <li>Long-form and short-form video production</li>
                <li>Channel management and community building</li>
              </ul>

              <h3 className="text-yellow-600 font-bold">Publishing Services</h3>
              <ul>
                <li>Book formatting and design</li>
                <li>Cover design and branding</li>
                <li>Amazon KDP setup and management</li>
                <li>Audiobook production and ACX compliance</li>
                <li>Publishing strategy and marketing</li>
              </ul>

              <h3 className="text-yellow-600 font-bold">SEO & Digital Visibility Services</h3>
              <ul>
                <li>Free instant SEO website audit (publicly available analysis tool)</li>
                <li>SEO management packages (Audit + Plan, Done-For-You SEO, Authority Programme tiers)</li>
                <li>Technical SEO audits, fixes, and ongoing optimisation</li>
                <li>AI search visibility optimisation (ChatGPT, Perplexity, Claude)</li>
                <li>Voice search readiness and schema markup implementation</li>
                <li>Content strategy, keyword research, and blog automation</li>
                <li>Multi-site SEO management via the SEO Command Center dashboard</li>
              </ul>

              <h3 className="text-yellow-600 font-bold">Website & Mobile App Services</h3>
              <ul>
                <li>Custom website design and development</li>
                <li>Mobile app development</li>
                <li>Progressive web app (PWA) creation</li>
              </ul>

              <h2 className="text-red-600 font-black">4. Free SEO Audit Tool</h2>
              
              <p className="text-gray-900">
                Our free SEO audit tool is provided as a complimentary service. By using this tool, you acknowledge and agree that:
              </p>
              <ul>
                <li><strong>Website Crawling:</strong> You authorise Audio & Co.® to crawl and analyse the publicly available pages of the website URL you provide. We only access information that is already publicly visible</li>
                <li><strong>Results Accuracy:</strong> Audit results are generated through automated analysis and are provided for informational purposes. While we strive for accuracy, results may vary depending on website technology, hosting firewalls, and other factors. Results should not be considered professional advice without further consultation</li>
                <li><strong>Data Collection:</strong> Your name, email address, website URL, consent preferences, and audit results are stored securely. See our <Link to="/privacy" className="text-red-600 font-bold hover:underline">Privacy Policy</Link> for full details</li>
                <li><strong>No Guarantee of Rankings:</strong> The audit identifies areas for improvement but does not guarantee any specific search engine ranking outcome. SEO results depend on many factors outside our control</li>
                <li><strong>Intellectual Property:</strong> The audit report format, scoring methodology, and recommendations are the intellectual property of Audio & Co.® and may not be resold or white-labelled without written permission</li>
                <li><strong>Fair Use:</strong> The free audit tool is intended for legitimate business use. Automated bulk submissions, scraping of our audit tool, or abuse of the service may result in access being restricted</li>
              </ul>

              <h2 className="text-red-600 font-black">5. SEO Management Services</h2>
              
              <p className="text-gray-900">
                For paid SEO management packages (Audit + Plan, Done-For-You SEO, Authority Programme):
              </p>
              <ul>
                <li><strong>Access Requirements:</strong> You agree to provide necessary access to your website (CMS login, hosting panel, Google Search Console) to enable us to perform the agreed services</li>
                <li><strong>Monthly Subscription:</strong> SEO management packages are billed monthly. You may cancel at any time with 30 days' written notice</li>
                <li><strong>Results Disclaimer:</strong> While we apply industry best practices, we cannot guarantee specific search engine rankings, traffic volumes, or revenue outcomes. SEO is a long-term strategy influenced by many factors including competition, algorithm changes, and market conditions</li>
                <li><strong>Reporting:</strong> You will receive regular reports detailing work performed, site health metrics, and recommendations. Frequency depends on your chosen package tier</li>
                <li><strong>Third-Party Platforms:</strong> We may access and make changes to third-party platforms on your behalf (WordPress, Google Search Console, etc.). You retain full ownership and control of all accounts</li>
              </ul>

              <h2 className="text-red-600 font-black">6. Service Agreements and Scope</h2>
              
              <p className="text-gray-900">
                Each project begins with a detailed Statement of Work (SOW) or Service Agreement that specifies:
              </p>
              <ul>
                <li>Exact deliverables and timelines</li>
                <li>Project scope and limitations</li>
                <li>Client responsibilities and requirements</li>
                <li>Payment terms and schedule</li>
                <li>Revision and approval processes</li>
              </ul>
              
              <p className="text-gray-900">
                Services outside the agreed scope will require a separate agreement and additional fees.
              </p>

              <div className="bg-red-50 border-l-4 border-red-600 p-6 mt-8">
                <div className="flex">
                  <CheckCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-black text-red-600 mb-2">Ready to Get Started?</h3>
                    <p className="text-red-700 text-sm mb-3 font-bold">
                      Now that you understand our terms, let's discuss how we can help build your media empire.
                    </p>
                    <Link 
                      to="/contact"
                      className="inline-flex items-center bg-red-600 text-white px-4 py-2 rounded-lg font-black hover:bg-red-700 transition-colors text-sm border-2 border-yellow-400"
                    >
                      Start Your Project
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Links */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="flex space-x-6 text-sm">
                  <Link to="/privacy" className="text-red-600 font-bold hover:underline">
                    Privacy Policy
                  </Link>
                  <Link to="/contact" className="text-red-600 font-bold hover:underline">
                    Contact Us
                  </Link>
                  <Link to="/" className="text-red-600 font-bold hover:underline">
                    Homepage
                  </Link>
                </div>
                <div className="text-sm text-gray-500">
                  Last updated: April 2026
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;